//@flow
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact, {
    formatComponentStack
} from '@bugsnag/plugin-react';
import NewRelic from 'common/utils/NewRelic';

import ProductTrackerService from 'common/services/ProductTrackerService';
import MFETrackerService from 'common/services/MFETrackerService';
import { isWorkerThread } from 'common/utils/webWorkerUtil';
import { type ProductType } from 'common/enums/Product';
import EnvConfig from 'config';

type User = {
    id: string,
    clientId: string
};
class ErrorReporter {
    bugsnagClient: ?$FixMe = null;
    isInitialized: boolean = false;
    user: ?User = null;

    constructor() {
        this.initialize();
    }

    initialize() {
        if (
            process.env.NODE_ENV !== 'test' &&
            // Bugsnag not support in worker thread see (https://github.com/bugsnag/bugsnag-js/issues/1299)
            // we use worker onError to report bugs via main thread, so not initializing error reporter in worker thread
            !isWorkerThread() &&
            !this.isInitialized
        ) {
            this.bugsnagClient = Bugsnag.start({
                apiKey: EnvConfig.bugsnagApiKey,
                // eslint-disable-next-line no-undef
                appVersion: __VERSION__,
                releaseStage: EnvConfig.releaseStage,
                plugins: [new BugsnagPluginReact()],
                onError: event => {
                    if (this.user) {
                        event.setUser(this.user.id);
                        event.addMetadata(
                            'react',
                            'clientId',
                            this.user.clientId
                        );
                    }

                    const activeProduct =
                        // if error is tagged to some product, use that
                        event.originalError?.product ||
                        ProductTrackerService.getActiveProduct();

                    const appConfig = MFETrackerService.getAppConfig(
                        activeProduct
                    );

                    if (appConfig) {
                        event.apiKey = appConfig.bugsnagApiKey;
                        event.app.version = appConfig.version;
                    }

                    event.addMetadata('react', 'product', activeProduct);

                    // send all registered info in metadata
                    const registeredApps = MFETrackerService.getRegisteredApps();
                    if (registeredApps.length > 0) {
                        event.addMetadata(
                            'context',
                            'apps',
                            registeredApps.map(app => ({
                                name: app.name,
                                version: app.version,
                                products: app.products
                            }))
                        );
                    }

                    return !this._shouldIgnoreError(
                        event.errors[0].errorMessage
                    );
                }
            });
            this.isInitialized = true;
        }
    }

    setUser(user: User) {
        this.user = user;
    }

    notify(
        error: Error,
        {
            metadata,
            product,
            errorName
        }: ?{
            errorName?: string,
            product?: ProductType,
            metadata?: ?{ [key: string]: any }
        } = {}
    ) {
        if (errorName) {
            error.name = errorName;
        }
        if (product) {
            error.product = product;
        }

        if (!this.isInitialized) {
            // eslint-disable-next-line no-console
            console.error(error);
            return;
        }

        Bugsnag.notify(error, event => {
            if (metadata) {
                event.addMetadata('context', metadata);
            }
        });

        NewRelic.notifyError(error);
    }

    notifyErrorBoundaryError(error, info) {
        if (!this.isInitialized) {
            // eslint-disable-next-line no-console
            console.error(error);
            return;
        }

        if (this.bugsnagClient) {
            const handledState = {
                severity: 'error',
                unhandled: true,
                severityReason: { type: 'unhandledException' }
            };
            const event = new this.bugsnagClient.Event.create(
                error,
                true,
                handledState,
                1
            );
            const metadata = { ...info };
            if (info && info.componentStack) {
                metadata.componentStack = formatComponentStack(
                    info.componentStack
                );
            }
            event.addMetadata('react', metadata);
            this.bugsnagClient._notify(event);
        }
        NewRelic.notifyError(error);
    }

    _shouldIgnoreError(errorMessage: string): boolean {
        return [
            // mara specific errors, happens when we are not connected to vpn
            'This API call is not allowed from the current ip.',
            // when user is not connected to internet
            'Internet Not Available',
            // 401 error, when token is invalid
            'Invalid credentials',
            'Failed to fetch'
        ].includes(errorMessage);
    }
}

export default (new ErrorReporter(): ErrorReporter);
